import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { validateFormSchema } from '@kfolks/models'
import type { FormSchema } from '@kfolks/types'
import { requestCenterApi } from '@/features/request-center'

/**
 * Debounced live validation of the in-progress `formSchema` against the
 * shared `validateFormSchema` from `@kfolks/models` — the same validator
 * the backend runs on save.
 *
 * Returns errors partitioned into per-field and top-level slots so
 * consumers can render them where they're useful:
 *
 *   - `errorsByFieldId[id]`: errors whose path is `fields[<n>].*` for the
 *     field with that id. Consumed by the canvas (per-card warning badge)
 *     and the properties panel (inline error per editor).
 *   - `topLevelErrors`: everything else (path = `''`, `'version'`,
 *     `'fields'` itself when it isn't an array, etc.). Consumed by the
 *     canvas's banner above the field list.
 *
 * Debounced at 250 ms (same window as the summary-template input). The
 * `knownDataSourceKeys` come from the cached `listDataSources` query —
 * shared with the properties-panel data-source select, so no extra
 * network call.
 *
 * **Do not** duplicate this hook into other consumers. Mount it once at
 * the top level and pass results down via shared state or props. Two
 * consumers debouncing in parallel would produce conflicting validation
 * snapshots and double the work for no benefit.
 */
export interface FormSchemaValidationView {
	/** Per-field error messages, keyed by field id. Empty array when valid. */
	errorsByFieldId: Record<string, string[]>
	/** Errors not attached to a specific field (top-level shape, version, etc.). */
	topLevelErrors: string[]
	/** True when the validator has at least one severity-`error` entry. */
	hasErrors: boolean
	/** True between an edit and the debounced re-validation (helpful for UX hints). */
	isPending: boolean
}

const EMPTY_VIEW: FormSchemaValidationView = {
	errorsByFieldId: {},
	topLevelErrors: [],
	hasErrors: false,
	isPending: false,
}

export function useFormSchemaValidation(formSchema: FormSchema): FormSchemaValidationView {
	const dataSourcesQuery = useQuery({
		queryKey: ['data-sources'],
		queryFn: () => requestCenterApi.listDataSources(),
	})
	const knownDataSourceKeys = useMemo(
		() => (dataSourcesQuery.data ?? []).map((d) => d.sourceKey),
		[dataSourcesQuery.data],
	)

	const [debouncedSchema, setDebouncedSchema] = useState<FormSchema>(formSchema)
	const [isPending, setIsPending] = useState(false)
	useEffect(() => {
		setIsPending(true)
		const handle = window.setTimeout(() => {
			setDebouncedSchema(formSchema)
			setIsPending(false)
		}, 250)
		return () => window.clearTimeout(handle)
	}, [formSchema])

	const view = useMemo<FormSchemaValidationView>(() => {
		// Until data sources resolve, run the validator with an empty allow-list.
		// This will (correctly) flag any field that references a `dataSource`
		// before the catalog loads — which the FE smooths over by ignoring
		// errors during the initial pending window. Acceptable: validation
		// is a UX hint, not a save gate (the backend re-runs it authoritatively).
		const result = validateFormSchema(debouncedSchema, { knownDataSourceKeys })
		const errorsByFieldId: Record<string, string[]> = {}
		const topLevelErrors: string[] = []
		for (const err of result.errors) {
			const fieldId = extractFieldIdFromPath(err.path, debouncedSchema)
			if (fieldId !== null) {
				const list = errorsByFieldId[fieldId] ?? []
				list.push(err.message)
				errorsByFieldId[fieldId] = list
			} else {
				topLevelErrors.push(err.message)
			}
		}
		return {
			errorsByFieldId,
			topLevelErrors,
			hasErrors: result.errors.some((e) => e.severity === 'error'),
			isPending,
		}
	}, [debouncedSchema, knownDataSourceKeys, isPending])

	// Suppress all output until the data-sources catalog has loaded — we
	// don't want false positives ("unknown data source") to flash during
	// the initial render. The validator still runs; consumers just get the
	// empty view until the cache warms up.
	if (dataSourcesQuery.isPending) return { ...EMPTY_VIEW, isPending: true }

	return view
}

/**
 * Translate a `ValidationError.path` string like `fields[2].label` or
 * `fields[2].conditions[0].field` into the corresponding field id by
 * looking up the index in the schema. Returns `null` for top-level paths
 * (`''`, `'version'`, `'fields'`).
 */
function extractFieldIdFromPath(path: string, schema: FormSchema): string | null {
	const match = /^fields\[(\d+)\]/.exec(path)
	if (!match) return null
	const idx = Number.parseInt(match[1] ?? '', 10)
	if (Number.isNaN(idx)) return null
	const field = schema.fields[idx]
	return field?.id ?? null
}
